"use client";

import { useEffect, useState } from "react";
import { useSession } from "next-auth/react";
import { API_URL, authOnlyHeaders } from "../../utils/api";
import {
  discardLocalReminders,
  migrateLocalReminders,
  readReminders,
  type ReminderRecord,
} from "../../utils/reminderStore";

export default function LegacyReminderMigration() {
  const { data: session } = useSession();
  const token = (session?.user as { accessToken?: string } | undefined)?.accessToken;
  if (!token) return null;
  return <LegacyReminderPrompt key={token} token={token} />;
}

function LegacyReminderPrompt({ token }: { token: string }) {
  const [records, setRecords] = useState<ReminderRecord[]>(() => Object.values(readReminders()));
  const [titles, setTitles] = useState<Record<string, string>>({});
  const [dismissed, setDismissed] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token || records.length === 0) return;
    let cancelled = false;
    void fetch(`${API_URL}/documents`, { headers: authOnlyHeaders(token) })
      .then((response) => response.ok ? response.json() : [])
      .then((documents: Array<{ id?: string; title?: string }>) => {
        if (cancelled || !Array.isArray(documents)) return;
        setTitles(Object.fromEntries(documents
          .filter((document) => document.id)
          .map((document) => [document.id as string, document.title || "제목 없음"])));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [records, token]);

  if (records.length === 0 || dismissed) return null;

  const importRecords = async () => {
    setImporting(true);
    setError("");
    const imported = await migrateLocalReminders(token);
    setImporting(false);
    if (imported) setRecords([]);
    else setError("일정을 가져오지 못했습니다. 로컬 일정은 유지됩니다.");
  };

  const discardRecords = () => {
    discardLocalReminders();
    setRecords([]);
  };

  return (
    <div className="fixed inset-0 z-100 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm" onClick={() => setDismissed(true)}>
      <div className="w-full max-w-lg rounded-xl border border-gray-200 bg-white p-6 shadow-2xl dark:border-zinc-800 dark:bg-zinc-900" onClick={(event) => event.stopPropagation()}>
        <h2 className="text-base font-bold text-gray-900 dark:text-zinc-100">이 브라우저의 예전 일정을 가져올까요?</h2>
        <p className="mt-2 text-sm leading-relaxed text-gray-600 dark:text-zinc-400">
          어느 계정이 만든 일정인지 확인할 수 없습니다. 내 일정이 맞을 때만 가져오세요.
        </p>

        <div className="mt-4 max-h-64 space-y-2 overflow-y-auto rounded-lg bg-gray-50 p-3 dark:bg-zinc-800/70">
          {records.map((record) => (
            <div key={record.id} className="text-sm text-gray-700 dark:text-zinc-200">
              <div className="font-medium">{titles[record.docId] || `문서 ${record.docId}`}</div>
              <div className="text-xs text-gray-500 dark:text-zinc-400">
                {Number.isNaN(Date.parse(record.date)) ? record.date : new Date(record.date).toLocaleString("ko-KR")}
              </div>
            </div>
          ))}
        </div>

        {error && <p role="alert" className="mt-3 text-sm text-red-600">{error}</p>}
        <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:justify-end">
          <button type="button" disabled={importing} onClick={() => setDismissed(true)} className="rounded-lg px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 disabled:opacity-50 dark:text-zinc-300 dark:hover:bg-zinc-800">
            나중에
          </button>
          <button type="button" disabled={importing} onClick={discardRecords} className="rounded-lg px-4 py-2 text-sm text-red-600 hover:bg-red-50 disabled:opacity-50 dark:hover:bg-red-950/30">
            예전 일정 삭제
          </button>
          <button type="button" disabled={importing} onClick={importRecords} className="rounded-lg bg-[#006DBA] px-4 py-2 text-sm font-bold text-white hover:bg-[#005A9E] disabled:opacity-50">
            {importing ? "가져오는 중…" : `${records.length}개 가져오기`}
          </button>
        </div>
      </div>
    </div>
  );
}
